import redaxios from 'redaxios';

import { API_URL } from '../constants';
import {
  TradeRequest,
  TradeRequestBody
} from '../types/responses/trade-response';
import { getUrl } from './http-client';

/**
 * Applies a POST to the trade state manager event.
 * @param gameCode - game code
 * @param body - offerPlayerCode as trade requester player and requestPlayerCode as trade destination player
 * @returns the trade request object.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods/POST
 */
export const PostTradeRequestOrConfirmAsync = async (
  gameCode: string,
  action: 'requests' | 'confirm',
  body: TradeRequestBody
) => {
  try {
    const url = getUrl(`${API_URL.gamesV2}/${gameCode}/trades/${action}`);
    const response = await redaxios.post(url, body);
    return response?.data as TradeRequest;
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
    }
    throw e;
  }
};

/**
 * Applies a POST to the trade state manager event.
 * @param gameCode - game code
 * @param tradeId - tradeId that will be rejected
 * @returns the trade request object.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods/POST
 */
export const PostTradeRejectAsync = async (
  gameCode: string,
  tradeId: string
) => {
  try {
    const url = getUrl(
      `${API_URL.gamesV2}/${gameCode}/trades/${tradeId}/respond/reject`
    );
    const response = await redaxios.post(url);
    return response?.data as TradeRequest;
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
    }
    throw e;
  }
};

/**
 * Applies a POST to the trade state manager event.
 * @param gameCode - game code
 * @param tradeId - trade id
 * @param action - accept, cancel, counter
 * @returns the trade request object.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods/POST
 */
export const PostTradeActionAsync = async (
  gameCode: string,
  tradeId: string,
  role: 'offer' | 'respond',
  action: 'accept' | 'cancel' | 'counter'
) => {
  try {
    const url = getUrl(
      `${API_URL.gamesV2}/${gameCode}/trades/${tradeId}/${role}/${action}`
    );
    const response = await redaxios.post(url);
    return response?.data as TradeRequest;
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
    }
    throw e;
  }
};

/**
 * Applies a POST to the trade state manager event.
 * @param gameCode - game code
 * @param tradeId - trade id
 * @param role - as offered player or respond player
 * @param body - item that will be send
 * @returns the trade request object.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods/POST
 */
export const PostTradeOfferOrRespondAsync = async (
  gameCode: string,
  tradeId: string,
  role: 'offer' | 'respond',
  body: {
    itemId: string;
    quantity: number;
    isOffered: boolean;
  }[]
) => {
  try {
    const url = getUrl(
      `${API_URL.gamesV2}/${gameCode}/trades/${tradeId}/${role}`
    );
    const response = await redaxios.post(url, body);
    return response?.data as TradeRequest;
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
    }
    throw e;
  }
};
